import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Info from '@mui/icons-material/Info';
import Warning from '@mui/icons-material/Warning';
import { alpha, Box, ButtonProps, Stack, Typography, useTheme } from '@mui/material';
import { DefaultDialog, DefaultDialogProps } from 'components/shared/DefaultDialog/DefaultDialog';
import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { exists } from 'typeDeclarations/typeGuards';

type Variant = 'default' | 'info' | 'warning' | 'success' | 'delete';

type VariantOpts = {
  icon?: JSX.Element;
  iconColorBg?: string;
  secondaryButtonColor: ButtonProps['color'];
  primaryButtonColor: ButtonProps['color'];
};

interface MiniDialogProps extends DefaultDialogProps {
  loading?: boolean;
  variant?: Variant;
  onClick?: () => void;
  description?: ReactNode;
  subDescription?: ReactNode;
  primaryButtonText?: ReactNode;
  secondaryButtonText?: ReactNode;
}

export const MiniDialog: FunctionComponent<PropsWithChildren<MiniDialogProps>> = ({
  title,
  onClose,
  onClick,
  description,
  subDescription,
  loading = false,
  primaryButtonText,
  secondaryButtonText,
  variant = 'default',
  ...dialogProps
}) => {
  const { palette } = useTheme();

  const variantOpts: Record<Variant, VariantOpts> = {
    default: {
      primaryButtonColor: 'secondary',
      secondaryButtonColor: 'secondary',
    },
    info: {
      primaryButtonColor: 'secondary',
      secondaryButtonColor: 'secondary',
      iconColorBg: alpha(palette.info.main, 0.12),
      icon: <Info fontSize="small" color="info" />,
    },
    delete: {
      primaryButtonColor: 'error',
      secondaryButtonColor: 'error',
      iconColorBg: alpha(palette.error.main, 0.12),
      icon: <Delete fontSize="small" color="error" />,
    },
    warning: {
      primaryButtonColor: 'secondary',
      secondaryButtonColor: 'secondary',
      iconColorBg: alpha(palette.warning.main, 0.12),
      icon: <Warning fontSize="small" color="warning" />,
    },
    success: {
      primaryButtonColor: 'secondary',
      secondaryButtonColor: 'secondary',
      iconColorBg: alpha(palette.success.main, 0.12),
      icon: <CheckCircle fontSize="small" color="success" />,
    },
  };

  let dialogActionsProps: DefaultDialogProps['dialogActionsProps'];

  if (primaryButtonText || secondaryButtonText) {
    dialogActionsProps = {};

    if (secondaryButtonText) {
      dialogActionsProps.secondaryButtonProps = {
        onClick: onClose,
        disabled: loading,
        children: secondaryButtonText,
        color: variantOpts[variant].secondaryButtonColor,
      };
    }

    if (primaryButtonText) {
      // TODO (LSC): Investigate the impact of e?.preventDefault() and e?.stopPropagation()?, also this type of button not being submit
      dialogActionsProps.primaryButtonProps = {
        type: 'button',
        loading,
        onClick: (e) => {
          e?.preventDefault();
          e?.stopPropagation();
          onClick?.();
        },
        children: primaryButtonText,
        color: variantOpts[variant].primaryButtonColor,
      };
    }
  }

  return (
    <DefaultDialog
      maxWidth="xs"
      onClose={onClose}
      disableEscapeKeyDown={loading}
      aria-labelledby="mini-dialog-title"
      dialogActionsProps={dialogActionsProps}
      aria-describedby="mini-dialog-description"
      {...dialogProps}
      title={
        <Stack direction="row" alignItems="center" columnGap={1}>
          {variantOpts[variant].icon && (
            <Box
              sx={{
                p: 0.5,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '100%',
                alignSelf: 'flex-start',
                justifyContent: 'center',
                backgroundColor: variantOpts[variant].iconColorBg,
              }}
            >
              {variantOpts[variant].icon}
            </Box>
          )}
          {title}
        </Stack>
      }
      content={
        (exists(description) || exists(subDescription)) && (
          <>
            {exists(description) && (
              <Typography component="div" color="textSecondary" id="mini-dialog-description">
                {description}
              </Typography>
            )}
            {exists(subDescription) && (
              <Typography component="div" color="textSecondary" id="mini-dialog-sub-description">
                {subDescription}
              </Typography>
            )}
          </>
        )
      }
    />
  );
};
